import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserSkill } from 'api/users/models';
import { Error } from 'components/forms/formik-prime/styled';
import { PrimeTextInput } from 'components/prime';
import { NewPrimeButton } from 'components/prime/NewButton/NewButton';
import { addSkill } from 'fetchers/actions/skill/addSkill';
import { updateSkill } from 'fetchers/actions/skill/updateSkill';
import { Dialog } from 'primereact/dialog';
import { Box, Flex } from 'rebass';

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  skillList: string[];
  skill: UserSkill | null;
}

export const SkillModal = ({ isOpen, setIsOpen, skillList, skill }: Props) => {
  const [skillName, setSkillName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const { t } = useTranslation();

  useEffect(() => {
    setDefaultData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skill]);

  useEffect(() => {
    if (skillName.length > 0) {
      setError('');
    }
  }, [skillName]);

  const onClose = () => {
    setError('');
    setDefaultData();
    setIsOpen(false);
  };

  const setDefaultData = () => {
    if (skill) {
      setSkillName(skill.name);
    } else {
      setSkillName('');
    }
  };

  const onSubmit = () => {
    const _skill = skillName.trim();
    const list = skill
      ? skillList.filter((p) => p.toLowerCase() !== skill.name.toLowerCase())
      : skillList;
    if (!skillName.length) {
      setError(t('errors.required'));
    } else if (list.includes(_skill.toLowerCase())) {
      setError(t('errors.nameTaken'));
    } else {
      if (skill) {
        updateSkill(skillName, skill.id);
      } else {
        addSkill(skillName);
      }
      onClose();
    }
  };

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSubmit();
    }
  };

  return (
    <Dialog
      onHide={onClose}
      visible={isOpen}
      blockScroll={true}
      header={t(skill ? 'users.editSkill' : 'users.addSkill')}
    >
      <Flex minWidth="300px" flexDirection="column">
        <PrimeTextInput
          ishigh="false"
          iserror={error}
          value={skillName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setSkillName(event.target.value)
          }
          label={`${t('users.skillName')}*`}
          onKeyDown={onKeyPress}
          autoFocus
        />
        {error && <Error>{error}</Error>}
      </Flex>
      <Flex mt={4} justifyContent="flex-end">
        <Box>
          <NewPrimeButton label={t('common.cancel')} onClick={onClose} />
        </Box>
        <Box ml={2}>
          <NewPrimeButton label={t('common.save')} onClick={onSubmit} />
        </Box>
      </Flex>
    </Dialog>
  );
};
